import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SubpageFoodCostHeader } from '@/components/food-cost/SubpageFoodCostHeader'
import { FaqLearnArticleLink } from '@/features/aprender/FaqLearnArticleLink'
import { FAQ_CONCEPT_ARTICLE_IDS, FAQ_CONCEPT_KEYS } from '@/features/aprender/faqConceptArticleLinks'
import { OFFICIAL_SALES_PHONE, toOfficialSalesTelHref } from '@/lib/officialContact'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { ROUTES } from '@/navigation/constants'
import { absoluteLocalizedUrl } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { DEFAULT_SHARE_IMAGE } from '@/shared/lib/siteSeo'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import { SocialShareMeta } from '@/shared/ui/SocialShareMeta'
import '@/styles/food-cost-fonts.css'

const TIPI_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8'] as const

type OpenId = string | null

function FaqItem({
    id,
    question,
    answer,
    articleId,
    openId,
    onToggle,
}: {
    id: string
    question: string
    answer: string
    articleId?: string
    openId: OpenId
    onToggle: (id: string) => void
}) {
    const isOpen = openId === id

    return (
        <div className='border-t border-[#D8CFC0]'>
            <button
                type='button'
                aria-expanded={isOpen}
                onClick={() => onToggle(id)}
                className='flex min-h-11 w-full items-start justify-between gap-3.5 bg-transparent py-[18px] text-left'
            >
                <span className='text-base font-semibold leading-snug text-[#17130E] md:text-[17px]'>{question}</span>
                <span className='shrink-0 text-[22px] font-medium leading-none text-[#F4502A]' aria-hidden>
                    {isOpen ? '−' : '+'}
                </span>
            </button>
            <div
                className={`max-w-[34ch] pb-[18px] text-[15px] leading-[1.65] text-[#5C5548] md:max-w-[52ch] ${isOpen ? '' : 'hidden'}`}
                aria-hidden={!isOpen}
            >
                {answer}
                {articleId ? <FaqLearnArticleLink articleId={articleId} /> : null}
            </div>
        </div>
    )
}

export function FaqPage() {
    const { t } = useTranslation()
    const locale = useLandingLocale()
    const [openId, setOpenId] = useState<OpenId>(null)

    const title = t('faqPage.meta.title', 'Tipi | Preguntas frecuentes')
    const description = t(
        'faqPage.meta.description',
        'Respuestas sobre Tipi, escandallos y food cost para hostelería independiente.',
    )
    const canonicalUrl = absoluteLocalizedUrl(ROUTES.faqs, locale)

    const faqEntities = [
        ...TIPI_KEYS.map(key => ({
            '@type': 'Question' as const,
            name: t(`faqPage.groups.tipi.items.${key}.question`),
            acceptedAnswer: {
                '@type': 'Answer' as const,
                text: t(`faqPage.groups.tipi.items.${key}.answer`),
            },
        })),
        ...FAQ_CONCEPT_KEYS.map(key => ({
            '@type': 'Question' as const,
            name: t(`faqPage.groups.concepts.items.${key}.question`),
            acceptedAnswer: {
                '@type': 'Answer' as const,
                text: t(`faqPage.groups.concepts.items.${key}.answer`),
            },
        })),
    ]

    const toggle = (id: string) => {
        setOpenId(current => (current === id ? null : id))
    }

    return (
        <div className="flex min-h-screen flex-col bg-[#F5EEE4] font-['Archivo',system-ui,sans-serif] text-[#17130E] antialiased">
            <LocaleSeoLinks path={ROUTES.faqs} locale={locale} />
            <SocialShareMeta title={title} description={description} imageUrl={DEFAULT_SHARE_IMAGE} url={canonicalUrl} />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
                <script type='application/ld+json'>
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        url: canonicalUrl,
                        name: title,
                        description,
                        mainEntity: faqEntities,
                    })}
                </script>
            </PageHelmet>

            <SubpageFoodCostHeader />

            <main className='mx-auto w-full max-w-[720px] flex-1 px-[22px] pb-12 pt-24 md:px-10 md:pb-16 md:pt-28'>
                <div className='mb-6 flex flex-col gap-2.5'>
                    <div className='text-xs font-semibold uppercase tracking-[2px] text-[#F4502A]'>
                        {t('faqPage.eyebrow')}
                    </div>
                    <h1 className='m-0 text-[32px] font-medium leading-[1.15] tracking-[-1px] text-[#17130E] md:text-[46px]'>
                        {t('faqPage.title')}
                    </h1>
                </div>

                <div className='mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A7F6F]'>
                    {t('faqPage.groups.tipi.label')}
                </div>
                <div>
                    {TIPI_KEYS.map(key => (
                        <FaqItem
                            key={`tipi-${key}`}
                            id={`p${key}`}
                            question={t(`faqPage.groups.tipi.items.${key}.question`)}
                            answer={t(`faqPage.groups.tipi.items.${key}.answer`)}
                            openId={openId}
                            onToggle={toggle}
                        />
                    ))}
                </div>

                <div className='mb-1 border-t border-[#D8CFC0] pt-7 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A7F6F]'>
                    {t('faqPage.groups.concepts.label')}
                </div>
                <div>
                    {FAQ_CONCEPT_KEYS.map(key => (
                        <FaqItem
                            key={`concepts-${key}`}
                            id={`c${key}`}
                            question={t(`faqPage.groups.concepts.items.${key}.question`)}
                            answer={t(`faqPage.groups.concepts.items.${key}.answer`)}
                            articleId={FAQ_CONCEPT_ARTICLE_IDS[key]}
                            openId={openId}
                            onToggle={toggle}
                        />
                    ))}
                </div>

                <p className='mt-0 border-t border-[#D8CFC0] pt-5 text-sm leading-relaxed text-[#5C5548]'>
                    {t('faqPage.contactPrefix')}{' '}
                    <a href={toOfficialSalesTelHref()} className='font-semibold text-[#F4502A] no-underline'>
                        {t('faqPage.contactPhone', OFFICIAL_SALES_PHONE)}
                    </a>
                    .
                </p>
            </main>

            <Footer landingPage='faqs' className='border-[#F5EEE4]/10 bg-[#17130E]' />
        </div>
    )
}
